// src/hooks/useSmartRebalance.js
import { useMemo } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';

const calculateTradeQuantity = ({currentValue, targetValue, avgCost, lastPrice}) => {
  const diffValue = Math.abs(targetValue - currentValue);
  
  let quantities = {
    byAvgCost: 0,
    byLastPrice: 0
  };

  try {
    if (lastPrice && lastPrice > 0) {
      quantities.byLastPrice = Math.floor(diffValue / lastPrice);
    }
    
    if (avgCost && avgCost > 0) {
      quantities.byAvgCost = Math.floor(diffValue / avgCost);
    } else if (lastPrice && lastPrice > 0) {
      quantities.byAvgCost = quantities.byLastPrice;
    }
  } catch (error) {
    console.error('Miktar hesaplama hatası:', error);
  }

  return quantities;
};

const prioritizeAlerts = (alerts, maxAlertsPerCategory = 2) => {
  const categorizedAlerts = {};
  alerts.forEach(alert => {
    if (!categorizedAlerts[alert.category]) categorizedAlerts[alert.category] = [];
    categorizedAlerts[alert.category].push(alert);
  });

  Object.keys(categorizedAlerts).forEach(category => {
    categorizedAlerts[category].sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
    categorizedAlerts[category] = categorizedAlerts[category].slice(0, maxAlertsPerCategory);
  });

  return Object.values(categorizedAlerts).flat();
};

export const useSmartRebalance = (portfolioData, settings, userEmail) => {
  const { convertAmount, selectedCurrency } = useCurrency();

  return useMemo(() => {
    if (!portfolioData?.targetPortfolio || !settings || !userEmail) {
      return [];
    }

    // Debug log
    console.log('SmartRebalance Running:', { 
      selectedCurrency, 
      totalValue: portfolioData.totalValue 
    });

    // Varlık değerini doğru para birimine çevir
    const getAssetValue = (asset) => {
      if (!asset) return 0;
      
      if (asset.symbol?.endsWith('.CURRENCY')) {
        const currency = asset.symbol.split('.')[0];
        const amount = parseFloat(asset.position) || parseFloat(asset.quantity) || 0;

        if (currency === selectedCurrency) {
          return amount;
        }
        
        return convertAmount(amount, currency, selectedCurrency);
      }

      return convertAmount(asset.value || 0, asset.currency || 'USD', selectedCurrency);
    };

    // Toplam değeri hesapla
    const calculateTotalValue = () => {
      return portfolioData.portfolioItems.reduce((total, category) => {
        if (category.subCategories?.length > 0) {
          return total + category.subCategories.reduce((subTotal, sub) => {
            return subTotal + (sub.items?.reduce((itemTotal, asset) => {
              const value = getAssetValue(asset);
              return itemTotal + value;
            }, 0) || 0);
          }, 0);
        }
        
        if (category.items?.length > 0) {
          return total + category.items.reduce((itemTotal, asset) => {
            const value = getAssetValue(asset);
            return itemTotal + value;
          }, 0);
        }

        return total;
      }, 0);
    };

    const alerts = [];
    const baseThreshold = settings.threshold || 5;
    const totalValue = calculateTotalValue();
    const targetBudget = convertAmount(settings.targetBudget || 1000000, 'USD', selectedCurrency);
    const isBelowBudget = totalValue < targetBudget;
    
    const buyThreshold = isBelowBudget ? baseThreshold * 0.5 : baseThreshold;
    const sellThreshold = baseThreshold;

    // Kategorileri kontrol et
    Object.entries(portfolioData.targetPortfolio).forEach(([categoryName, categoryData]) => {
      const category = portfolioData.portfolioItems.find(item => item.name === categoryName);
      if (!category) return;

      const categoryValue = category.subCategories?.reduce((sum, sub) => {
        return sum + (sub.items?.reduce((itemSum, asset) => {
          const value = getAssetValue(asset);
          return itemSum + value;
        }, 0) || 0);
      }, 0) || category.items?.reduce((sum, asset) => {
        const value = getAssetValue(asset);
        return sum + value;
      }, 0) || 0;

      const currentAllocation = totalValue > 0 ? (categoryValue / totalValue) * 100 : 0;
      const categoryDiff = categoryData.allocation - currentAllocation;
      const categoryAction = categoryDiff > 0 ? 'Al' : 'Sat';
      const appliedThreshold = categoryAction === 'Al' ? buyThreshold : sellThreshold;

      if (Math.abs(categoryDiff) >= appliedThreshold) {
        if (isBelowBudget && categoryAction === 'Sat') return;

        const alert = {
          category: categoryName,
          currentAllocation,
          targetAllocation: categoryData.allocation,
          difference: categoryDiff,
          value: categoryValue,
          targetValue: (categoryData.allocation / 100) * totalValue,
          action: categoryAction,
          isCurrency: categoryName?.endsWith('.CURRENCY'),
          currency: selectedCurrency,
          subCategories: [],
          assets: []
        };

        // Alt kategorileri kontrol et
        if (categoryData.subCategories && category.subCategories) {
          Object.entries(categoryData.subCategories).forEach(([subName, subData]) => {
            const subCategory = category.subCategories.find(sub => sub.name === subName);
            if (!subCategory) return;

            const subValue = subCategory.items?.reduce((sum, asset) => {
              const value = getAssetValue(asset);
              return sum + value;
            }, 0) || 0;

            const subCurrentAllocation = categoryValue > 0 ? (subValue / categoryValue) * 100 : 0;
            const subDiff = subData.allocation - subCurrentAllocation;
            const subAction = subDiff > 0 ? 'Al' : 'Sat';

            if (subAction === categoryAction && Math.abs(subDiff) >= appliedThreshold) {
              const subAlert = {
                category: `${categoryName} > ${subName}`,
                currentAllocation: subCurrentAllocation,
                targetAllocation: subData.allocation,
                difference: subDiff,
                value: subValue,
                targetValue: (subData.allocation / 100) * categoryValue,
                action: subAction,
                currency: selectedCurrency,
                assets: []
              };

              // Alt kategori varlıklarını kontrol et
              if (subData.assets && subCategory.items) {
                Object.entries(subData.assets).forEach(([symbol, assetData]) => {
                  const asset = subCategory.items.find(item => item.symbol === symbol);
                  if (!asset) return;

                  const assetValue = getAssetValue(asset);
                  const assetCurrentAllocation = subValue > 0 ? (assetValue / subValue) * 100 : 0;
                  const assetDiff = assetData.allocation - assetCurrentAllocation;
                  const assetAction = assetDiff > 0 ? 'Al' : 'Sat';

                  if (assetAction === subAction && Math.abs(assetDiff) >= appliedThreshold) {
                    const targetValue = (assetData.allocation / 100) * subValue;
                    const isCurrencyAsset = asset.symbol?.endsWith('.CURRENCY');
                    const assetCurrency = isCurrencyAsset ? asset.symbol.split('.')[0] : (asset.currency || 'USD');

                    let quantity, quantity_by_avg;
                    if (isCurrencyAsset && assetCurrency === selectedCurrency) {
                      quantity = Math.abs(targetValue - assetValue);
                      quantity_by_avg = quantity;
                    } else {
                      const quantities = calculateTradeQuantity({
                        currentValue: assetValue,
                        targetValue,
                        avgCost: asset.avgCost,
                        lastPrice: asset.lastPrice
                      });
                      quantity = quantities.byLastPrice || quantities.byAvgCost;
                      quantity_by_avg = quantities.byAvgCost;
                    }

                    subAlert.assets.push({
                      symbol,
                      currentAllocation: assetCurrentAllocation,
                      targetAllocation: assetData.allocation,
                      difference: assetDiff,
                      value: assetValue,
                      targetValue,
                      action: assetAction,
                      quantity,
                      quantity_by_avg,
                      estimatedCost: Math.abs(targetValue - assetValue),
                      category: `${categoryName} > ${subName}`,
                      avgCost: asset.avgCost,
                      lastPrice: asset.lastPrice,
                      isCurrency: isCurrencyAsset,
                      currency: selectedCurrency,
                      originalCurrency: assetCurrency,
                      rsi1h: asset.rsi1h,
                      ema7_1h: asset.ema7_1h,
                      ema21_1h: asset.ema21_1h,
                      ema55_1h: asset.ema55_1h,
                      ema100_1h: asset.ema100_1h
                    });
                  }
                });
              }

              if (subAlert.assets.length > 0) {
                alert.subCategories.push(subAlert);
              }
            }
          });
        }

        // Ana kategori varlıkları
        if (categoryData.assets && category.items) {
          Object.entries(categoryData.assets).forEach(([symbol, assetData]) => {
            const asset = category.items.find(item => item.symbol === symbol);
            if (!asset) return;

            const assetValue = getAssetValue(asset);
            const assetCurrentAllocation = categoryValue > 0 ? (assetValue / categoryValue) * 100 : 0;
            const assetDiff = assetData.allocation - assetCurrentAllocation;
            const assetAction = assetDiff > 0 ? 'Al' : 'Sat';

            if (assetAction === categoryAction && Math.abs(assetDiff) >= appliedThreshold) {
              const targetValue = (assetData.allocation / 100) * categoryValue;
              const isCurrencyAsset = asset.symbol?.endsWith('.CURRENCY');
              const assetCurrency = isCurrencyAsset ? asset.symbol.split('.')[0] : (asset.currency || 'USD');

              let quantity, quantity_by_avg;
              if (isCurrencyAsset && assetCurrency === selectedCurrency) {
                quantity = Math.abs(targetValue - assetValue);
                quantity_by_avg = quantity;
              } else {
                const quantities = calculateTradeQuantity({
                  currentValue: assetValue,
                  targetValue,
                  avgCost: asset.avgCost,
                  lastPrice: asset.lastPrice
                });
                quantity = quantities.byLastPrice || quantities.byAvgCost;
                quantity_by_avg = quantities.byAvgCost;
              }

              alert.assets.push({
                symbol,
                currentAllocation: assetCurrentAllocation,
                targetAllocation: assetData.allocation,
                difference: assetDiff,
                value: assetValue,
                targetValue,
                action: assetAction,
                quantity,
                quantity_by_avg,
                estimatedCost: Math.abs(targetValue - assetValue),
                category: categoryName,
                avgCost: asset.avgCost,
                lastPrice: asset.lastPrice,
                isCurrency: isCurrencyAsset,
                currency: selectedCurrency,
                originalCurrency: assetCurrency,
                rsi1h: asset.rsi1h,
                ema7_1h: asset.ema7_1h,
                ema21_1h: asset.ema21_1h,
                ema55_1h: asset.ema55_1h,
                ema100_1h: asset.ema100_1h
              });
            }
          });
        }

        alerts.push(alert);
      }
    });

    return prioritizeAlerts(alerts).sort((a, b) => {
      if (isBelowBudget && a.action !== b.action) {
        return a.action === 'Al' ? -1 : 1;
      }
      return Math.abs(b.difference) - Math.abs(a.difference);
    });
  }, [portfolioData, settings, userEmail, convertAmount, selectedCurrency]);
};